import { faCircleInfo, faStar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  ContextMenu,
  ContextMenuItem,
  ContextMenuList,
  Popover,
  PopoverContent,
  PopperTrigger,
  TextWithIcon,
  Toast,
  useOpenModal,
} from '@modusign/design-system';
import { useCreateBookmark, useRemoveBookmark } from '@modusign/openapi-client-category';
import { ReactElement } from 'react';
import { useQueryClient } from 'react-query';

import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

import LabelInfoModal from '../../../../LabelInfoModal';

interface LabelListItemMenuPopoverProps
  extends Pick<Parameters<typeof Popover>[0], 'isOpen' | 'onOpenChange'> {
  children: ReactElement;
  labelId: string;
  hasBookmarked: boolean;
}

function LabelListItemMenuPopover({
  isOpen,
  children,
  labelId,
  hasBookmarked,
  onOpenChange,
}: LabelListItemMenuPopoverProps) {
  const openModal = useOpenModal();

  const { createBookmark, removeBookmark, isMutating } = useLabelBookmark(labelId);

  return (
    <Popover gutter={0} isOpen={isOpen} mode="click" nested onOpenChange={onOpenChange}>
      <PopperTrigger>{children}</PopperTrigger>
      <PopoverContent>
        <Box bg="pureBlue25" width="fit-content">
          <ContextMenu isOpen={isOpen}>
            <ContextMenuList align="left" m={0}>
              <ContextMenuItem
                px={4}
                py={2}
                width="158px"
                onClick={() =>
                  openModal(({ onClose }) => <LabelInfoModal id={labelId} onClose={onClose} />)
                }
              >
                <TextWithIcon
                  leftIcon={
                    <Box as={FontAwesomeIcon} color="textNeutralLight" icon={faCircleInfo} />
                  }
                >
                  상세보기
                </TextWithIcon>
              </ContextMenuItem>
              <ContextMenuItem
                disabled={isMutating}
                px={4}
                py={2}
                width="158px"
                onClick={() => {
                  if (hasBookmarked) {
                    removeBookmark({ onSettled: () => onOpenChange?.(false) });
                  } else {
                    createBookmark({ onSettled: () => onOpenChange?.(false) });
                  }
                }}
              >
                <TextWithIcon
                  leftIcon={<Box as={FontAwesomeIcon} color="textNeutralLight" icon={faStar} />}
                >
                  중요 표시 {hasBookmarked && '해제'}
                </TextWithIcon>
              </ContextMenuItem>
            </ContextMenuList>
          </ContextMenu>
        </Box>
      </PopoverContent>
    </Popover>
  );
}

function useLabelBookmark(labelId: string) {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();

  const { mutate: createBookmark, isLoading: isCreating } = useCreateBookmark({
    mutation: {
      mutationKey: ['bookmark', labelId, 'create'],
      onError: () => {
        Toast.error({ description: '라벨 중요 표시 실패했습니다.' });
      },
      onSuccess: () => queryClient.invalidateQueries([`/workspaces/${workspaceId}/labels`]),
    },
  });

  const { mutate: removeBookmark, isLoading: isRemoving } = useRemoveBookmark({
    mutation: {
      mutationKey: ['bookmark', labelId, 'remove'],
      onError: () => {
        Toast.error({ description: '라벨 중요 표시 실패했습니다.' });
      },
      onSuccess: () => queryClient.invalidateQueries([`/workspaces/${workspaceId}/labels`]),
    },
  });

  return {
    isMutating: isCreating || isRemoving,
    createBookmark: createBookmark.bind(null, { workspaceId, labelId }),
    removeBookmark: removeBookmark.bind(null, { workspaceId, labelId }),
  };
}

export default LabelListItemMenuPopover;
